import React from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Briefcase, UserRound, Sparkles, EyeOff, ArrowRight } from 'lucide-react';

const targetData = {
  de: {
    sectionTitle: 'Für wen ist DHI ohne Rasur ideal?',
    sectionSubtitle: 'Die perfekte Lösung für alle, die maximale Diskretion wünschen',
    groups: [
      {
        icon: Briefcase,
        title: 'Berufstätige & Führungskräfte',
        description: 'Keine Ausfallzeit, keine sichtbaren Spuren. Am nächsten Tag direkt zurück ins Meeting.',
      },
      {
        icon: UserRound,
        title: 'Frauen mit dünnem Haar',
        description: 'Perfekt zur Verdichtung von Scheitel und Stirnbereich — ohne das bestehende Haar zu opfern.',
      },
      {
        icon: Sparkles,
        title: 'Verdichtung & Geheimratsecken',
        description: 'Ideal, wenn kein großer Kahlbereich, aber gezielte Verdichtung in bestimmten Zonen gewünscht ist.',
      },
      {
        icon: EyeOff,
        title: 'Maximale Diskretion gewünscht',
        description: 'Für alle, die nicht möchten, dass Freunde, Kollegen oder Familie von der Behandlung erfahren.',
      },
    ],
    ctaText: 'Bin ich geeignet? Jetzt beraten lassen',
  },
  en: {
    sectionTitle: 'Who is DHI without shaving ideal for?',
    sectionSubtitle: 'The perfect solution for everyone who wants maximum discretion',
    groups: [
      {
        icon: Briefcase,
        title: 'Professionals & Executives',
        description: 'No downtime, no visible traces. Back in the meeting the very next day.',
      },
      {
        icon: UserRound,
        title: 'Women with thinning hair',
        description: 'Perfect for densification of parting and forehead area — without sacrificing existing hair.',
      },
      {
        icon: Sparkles,
        title: 'Densification & Receding Hairlines',
        description: 'Ideal when no large bald area, but targeted densification in specific zones is desired.',
      },
      {
        icon: EyeOff,
        title: 'Maximum discretion desired',
        description: 'For everyone who doesn\'t want friends, colleagues, or family to know about the treatment.',
      },
    ],
    ctaText: 'Am I a candidate? Get a free consultation',
  },
};

const DhiTargetAudience = () => {
  const { features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();

  if (!features?.showDhiTargetAudience) return null;

  const t = targetData[language] || targetData.de;

  return (
    <section
      className={`py-20 md:py-28 relative overflow-hidden transition-colors duration-500 ${
        isDark ? 'bg-[#0d0d0d]' : 'bg-white'
      }`}
      data-testid="dhi-target-audience-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t.sectionTitle}
          </h2>
          <p className={`text-base lg:text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t.sectionSubtitle}
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
          {t.groups.map((group, idx) => {
            const Icon = group.icon;
            return (
              <div
                key={idx}
                className={`group relative rounded-2xl p-6 md:p-8 border transition-all duration-300 hover:scale-[1.02] ${
                  isDark
                    ? 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-[#7BA7C2]/30'
                    : 'border-gray-200 bg-gray-50/50 hover:bg-white hover:border-[#5A8BA6]/30 hover:shadow-lg'
                }`}
                data-testid={`target-audience-card-${idx}`}
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-5 transition-colors duration-300 ${
                  isDark
                    ? 'bg-[#7BA7C2]/10 text-[#7BA7C2] group-hover:bg-[#7BA7C2]/20'
                    : 'bg-[#5A8BA6]/10 text-[#5A8BA6] group-hover:bg-[#5A8BA6]/15'
                }`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {group.title}
                </h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {group.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <button
            onClick={() => { const el = document.querySelector('#kontakt'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white font-medium transition-all duration-300 hover:scale-105 bg-[#7BA7C2] hover:bg-[#6b97b2] shadow-lg hover:shadow-xl"
            data-testid="dhi-target-cta-btn"
          >
            {t.ctaText}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default DhiTargetAudience;
